import {
  ArrowRight,
  BedDouble,
  Droplets,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  WalletCards,
  Wifi,
} from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdPlacement } from '../components/AdPlacement';
import { HostelCard } from '../components/HostelCard';
import { SelectMenu } from '../components/ui/SelectMenu';
import { api } from '../lib/api';
import { applyBudgetRange, budgetRanges, occupancyOptions } from '../lib/searchOptions';
import type { AdCampaign, Campus, HostelSummary } from '../lib/types';

const quickFilters = [
  { label: 'Under GHS 3,000', icon: Sparkles, query: 'maxPrice=300000' },
  { label: 'Close to campus', icon: MapPin, query: 'distance=1500' },
  { label: 'Reliable water', icon: Droplets, query: 'amenity=water_storage' },
  { label: 'Private rooms', icon: BedDouble, query: 'occupancy=1' },
  { label: 'Wi-Fi', icon: Wifi, query: 'amenity=internet' },
  { label: 'Security', icon: ShieldCheck, query: 'amenity=security' },
];

export function HomePage() {
  const navigate = useNavigate();
  const [hostels, setHostels] = useState<HostelSummary[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [ads, setAds] = useState<AdCampaign[]>([]);
  const [campus, setCampus] = useState('uds-tamale-dungu');
  const [budget, setBudget] = useState('');
  const [occupancy, setOccupancy] = useState('');

  useEffect(() => {
    api.get<{ campuses: Campus[]; featuredHostels: HostelSummary[]; ads: AdCampaign[] }>('/api/public/bootstrap')
      .then(result => {
        setCampuses(result.campuses);
        setAds(result.ads ?? []);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ limit: '12' });
    if (campus) params.set('campus', campus);
    applyBudgetRange(params, budget);
    if (occupancy) params.set('occupancy', occupancy);
    api.get<{ items: HostelSummary[] }>(`/api/public/hostels?${params.toString()}`)
      .then(result => { if (!cancelled) setHostels(result.items); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [campus, budget, occupancy]);

  function search(event: FormEvent) {
    event.preventDefault();
    const params = new URLSearchParams();
    if (campus) params.set('campus', campus);
    applyBudgetRange(params, budget);
    if (occupancy) params.set('occupancy', occupancy);
    navigate(`/explore?${params.toString()}`);
  }

  const campusLabel = campuses.find(item => item.slug === campus)?.campus_name || (campus === 'uds-tamale-dungu' ? 'UDS Tamale' : 'your campus');

  return (
    <div className="marketplace-home">
      <section className="discovery-head">
        <div className="market-page">
          <div className="discovery-title-row">
            <div>
              <h1>Find hostels near campus.</h1>
            </div>
            <Link to="/requests/new" className="find-for-me-link"><Sparkles size={17}/><span><strong>Let agents help</strong></span><ArrowRight size={16}/></Link>
          </div>

          <form className="market-search" onSubmit={search}>
            <SelectMenu
              className="market-search-select"
              label="Campus"
              ariaLabel="Choose campus"
              icon={MapPin}
              value={campus}
              onChange={setCampus}
              options={[
                { value: 'uds-tamale-dungu', label: 'UDS · Tamale', description: 'Tamale Campus (Dungu)' },
                ...campuses.filter(item => item.slug !== 'uds-tamale-dungu').map(item => ({
                  value: item.slug,
                  label: item.slug === 'uds-nyankpala' ? 'UDS · Nyankpala' : item.university_name,
                  description: item.campus_name,
                })),
              ]}
            />
            <SelectMenu className="market-search-select" label="Annual budget" ariaLabel="Choose annual budget" icon={WalletCards} value={budget} onChange={setBudget} options={budgetRanges}/>
            <SelectMenu className="market-search-select" label="Room" ariaLabel="Choose room occupancy" icon={Users} value={occupancy} onChange={setOccupancy} options={occupancyOptions}/>
            <button aria-label="Search hostels"><Search size={18}/><span>Search</span></button>
          </form>

        </div>
      </section>

      <main className="market-page home-market-content">
        <section className="home-collection">
          <div className="collection-heading">
            <div><h2>Hostels near {campusLabel}</h2></div>
            <Link to="/explore">See all <ArrowRight size={16}/></Link>
          </div>
          <div className="hostel-grid-v2 home-inventory">{hostels.length ? hostels.map(hostel => <HostelCard key={hostel.id} hostel={hostel}/>) : <p className="home-inventory-empty">No hostels match these filters.</p>}</div>
        </section>

        <section className="owner-promo-band"><div className="owner-promo-icon"><ShieldCheck/></div><div><span className="eyebrow">Hostel owner?</span><h2>Claim your hostel page.</h2></div><Link className="secondary-button" to="/representative/access">Claim page <ArrowRight size={17}/></Link></section>

        <section className="home-collection quick-filter-section">
          <div className="collection-heading"><div><h2>Browse by what matters</h2><p>Start with a common student priority.</p></div></div>
          <div className="quick-filter-row">{quickFilters.map(({ label, icon: Icon, query }) => <Link to={`/explore?${query}`} key={label}><Icon size={18}/><span>{label}</span></Link>)}</div>
        </section>

        <section className="agent-request-band">
          <div className="agent-request-icon"><Sparkles/></div>
          <div><span>Not finding the right fit?</span><h2>Describe what you need once.</h2><p>A limited number of matched agencies can respond with suitable hostels, room availability and a private quote.</p></div>
          <Link className="primary-button" to="/requests/new">Create a hostel request <ArrowRight size={17}/></Link>
        </section>

        {ads[0] && <AdPlacement campaign={ads[0]} variant="strip"/>}
      </main>
    </div>
  );
}
